"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.normalizeDatabaseSearchQuery = normalizeDatabaseSearchQuery;
exports.databaseSearchCellText = databaseSearchCellText;
exports.matchesDatabaseSearchText = matchesDatabaseSearchText;
exports.matchesDatabaseRowSearch = matchesDatabaseRowSearch;
exports.parseDatabaseProjectionDate = parseDatabaseProjectionDate;
exports.isEmptyDatabaseProjectionValue = isEmptyDatabaseProjectionValue;
exports.prepareDatabaseProjectionPredicate = prepareDatabaseProjectionPredicate;
exports.matchesDatabaseProjectionPredicate = matchesDatabaseProjectionPredicate;
exports.normalizeDatabaseProjectionWindow = normalizeDatabaseProjectionWindow;
exports.createDatabaseProjectionAggregate = createDatabaseProjectionAggregate;
exports.calculateDatabaseProjectionAggregate = calculateDatabaseProjectionAggregate;
/** Shared database text-search semantics for projections, shard caches and embeds.
 * Each function is self-contained so sandbox consumers can embed its source.
 * Callers supply display-normalized cell values (for example resolved select labels).
 */
function normalizeDatabaseSearchQuery(query) {
    return query.trim().toLowerCase();
}
function databaseSearchCellText(value) {
    if (value == null)
        return '';
    if (typeof value === 'string')
        return value;
    if (typeof value === 'number' || typeof value === 'boolean')
        return String(value);
    const serialized = JSON.stringify(value);
    if (serialized === undefined)
        throw new TypeError('Unsupported database search cell value');
    return serialized;
}
/** normalizedQuery comes from normalizeDatabaseSearchQuery. */
function matchesDatabaseSearchText(text, normalizedQuery) {
    return text.toLowerCase().includes(normalizedQuery);
}
/**
 * Whether a cell matches the value a row is searched by: case-insensitive,
 * trimmed, an empty cell never. Row matching of automations searches with it,
 * on the platform and in the local run of `sl-streamkit`.
 */
function matchesDatabaseRowSearch(value, searchValue, mode) {
    if (value === null || value === undefined)
        return false;
    const normalizedSearch = searchValue.toLowerCase().trim();
    const cellText = String(value).toLowerCase().trim();
    switch (mode) {
        case 'exact':
            return cellText === normalizedSearch;
        case 'starts_with':
            return cellText.startsWith(normalizedSearch);
        case 'contains':
            return cellText.includes(normalizedSearch);
    }
}
function parseDatabaseProjectionDate(value) {
    if (value instanceof Date) {
        const time = value.getTime();
        return Number.isFinite(time) ? time : undefined;
    }
    if (typeof value === 'number') {
        return Number.isFinite(value) ? value : undefined;
    }
    if (typeof value === 'string' && value.trim()) {
        const time = new Date(value).getTime();
        return Number.isFinite(time) ? time : undefined;
    }
    return undefined;
}
function isEmptyDatabaseProjectionValue(value) {
    return (value === undefined ||
        value === null ||
        value === '' ||
        (Array.isArray(value) && value.length === 0));
}
/** Query-local immutable search setup, shared by streaming and one-shot callers. */
function prepareDatabaseProjectionPredicate(predicate, propertyIds) {
    const children = predicate.type === 'predicate-group'
        ? predicate.predicates.map(child => prepareDatabaseProjectionPredicate(child, propertyIds))
        : [];
    const needle = predicate.type === 'text-search'
        ? normalizeDatabaseSearchQuery(predicate.query)
        : '';
    const batches = [];
    if (predicate.type === 'text-search' && needle) {
        const searchable = predicate.propertyIds?.length
            ? predicate.propertyIds
            : propertyIds;
        for (let offset = 0; offset < searchable.length; offset += 8)
            batches.push(searchable.slice(offset, offset + 8));
    }
    return {
        work: {
            textPredicates: (predicate.type === 'text-search' ? 1 : 0) +
                children.reduce((n, child) => n + child.work.textPredicates, 0),
            cellBatches: batches.length +
                children.reduce((n, child) => n + child.work.cellBatches, 0),
        },
        matches(readValue, diagnostics, readSearchValue = readValue, prepareCells) {
            switch (predicate.type) {
                case 'column': {
                    const value = readValue(predicate.propertyId);
                    switch (predicate.operator) {
                        case 'boolean-is':
                            return Boolean(value) === Boolean(predicate.value);
                        case 'contains':
                            return matchesDatabaseSearchText(databaseSearchCellText(value), 
                            // Column contains matches the value as typed; only full-text
                            // search trims the query.
                            String(predicate.value ?? '').toLowerCase());
                        case 'equals':
                            return Object.is(value, predicate.value);
                        case 'is-empty':
                            return isEmptyDatabaseProjectionValue(value);
                        case 'is-not-empty':
                            return !isEmptyDatabaseProjectionValue(value);
                        case 'not-equals':
                            return !Object.is(value, predicate.value);
                    }
                }
                case 'predicate-group':
                    return predicate.operator === 'or'
                        ? children.some(child => child.matches(readValue, diagnostics, readSearchValue, prepareCells))
                        : children.every(child => child.matches(readValue, diagnostics, readSearchValue, prepareCells));
                case 'temporal-overlap': {
                    const windowStart = parseDatabaseProjectionDate(predicate.windowStart);
                    const windowEnd = parseDatabaseProjectionDate(predicate.windowEnd);
                    if (windowStart === undefined || windowEnd === undefined) {
                        diagnostics.push({
                            code: 'invalid-date',
                            message: 'Temporal projection requires a valid time window.',
                            path: 'predicates.temporal-overlap',
                        });
                        return false;
                    }
                    if (windowStart > windowEnd) {
                        diagnostics.push({
                            code: 'invalid-window',
                            message: 'Temporal projection window must not end before it starts.',
                            path: 'predicates.temporal-overlap',
                        });
                        return false;
                    }
                    const start = parseDatabaseProjectionDate(readValue(predicate.startPropertyId));
                    const end = parseDatabaseProjectionDate(predicate.endPropertyId
                        ? readValue(predicate.endPropertyId)
                        : undefined);
                    if (start === undefined || windowStart === windowEnd)
                        return false;
                    const normalizedEnd = Math.max(start, end ?? start);
                    return (start < windowEnd &&
                        (normalizedEnd > windowStart || start >= windowStart));
                }
                case 'text-search': {
                    if (!needle)
                        return true;
                    for (const batch of batches) {
                        prepareCells?.(batch);
                        if (batch.some(propertyId => matchesDatabaseSearchText(databaseSearchCellText(readSearchValue(propertyId)), needle)))
                            return true;
                    }
                    return false;
                }
            }
        },
    };
}
/** One-shot entry point; streaming consumers prepare once per query. */
function matchesDatabaseProjectionPredicate(predicate, readValue, propertyIds, diagnostics, readSearchValue = readValue, prepareCells) {
    return prepareDatabaseProjectionPredicate(predicate, propertyIds).matches(readValue, diagnostics, readSearchValue, prepareCells);
}
function normalizeDatabaseProjectionWindow(window, totalCount, diagnostics) {
    const offset = window?.offset ?? 0;
    const limit = window?.limit ?? totalCount;
    if (!Number.isInteger(offset) || offset < 0) {
        diagnostics.push({
            code: 'invalid-window',
            message: 'Projection window offset must be a non-negative integer.',
            path: 'window.offset',
        });
        return { limit: 0, offset: 0 };
    }
    if (!Number.isInteger(limit) || limit < 0) {
        diagnostics.push({
            code: 'invalid-window',
            message: 'Projection window limit must be a non-negative integer.',
            path: 'window.limit',
        });
        return { limit: 0, offset: 0 };
    }
    return { limit, offset };
}
/** Constant-memory aggregate over a source split into revision-bound chunks. */
function createDatabaseProjectionAggregate(request) {
    let count = 0;
    let numericCount = 0;
    let sum = 0;
    let minimum = Infinity;
    let maximum = -Infinity;
    return {
        add(value) {
            count += 1;
            if (request.type === 'count' || !request.propertyId)
                return;
            const number = Number(value);
            if (!Number.isFinite(number))
                return;
            numericCount += 1;
            sum += number;
            minimum = Math.min(minimum, number);
            maximum = Math.max(maximum, number);
        },
        result(complete) {
            const value = request.type === 'count'
                ? count
                : !request.propertyId || numericCount === 0
                    ? null
                    : request.type === 'avg'
                        ? sum / numericCount
                        : request.type === 'min'
                            ? minimum
                            : request.type === 'max'
                                ? maximum
                                : sum;
            return {
                complete: complete && (request.type === 'count' || Boolean(request.propertyId)),
                id: request.id,
                type: request.type,
                value,
            };
        },
    };
}
function calculateDatabaseProjectionAggregate(request, values, complete) {
    const accumulator = createDatabaseProjectionAggregate(request);
    for (const value of values)
        accumulator.add(value);
    return accumulator.result(complete);
}
